import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useSessionStore } from '../../stores/sessionStore';
import { MATRIX_CELLS } from '@range-reader/poker-core';
import type { RangeSnapshot } from '@range-reader/shared';

const CELL_SIZE = 8;
const GRID_PX = 13 * CELL_SIZE;

const STREET_COLORS: Record<string, string> = {
  preflop: '#6366f1',
  flop: '#10b981',
  turn: '#f59e0b',
  river: '#ef4444',
};

function getEliminatedCategories(snap: RangeSnapshot): Set<string> {
  return new Set(
    snap.matrixState
      .map((s, i) => s === 'ELIMINATED' ? (MATRIX_CELLS[i]?.category ?? '') : '')
      .filter(Boolean)
  );
}

function MiniGrid({ snapshot, prevSnapshot }: { snapshot: RangeSnapshot; prevSnapshot?: RangeSnapshot }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const highlighted = new Set(snapshot.highlightedCategories);
    const eliminated = getEliminatedCategories(snapshot);
    const prevEliminated = prevSnapshot ? getEliminatedCategories(prevSnapshot) : new Set<string>();
    const newlyEliminated = new Set([...eliminated].filter(h => !prevEliminated.has(h)));

    ctx.clearRect(0, 0, GRID_PX, GRID_PX);

    MATRIX_CELLS.forEach((cell, idx) => {
      const row = Math.floor(idx / 13);
      const col = idx % 13;
      const x = col * CELL_SIZE;
      const y = row * CELL_SIZE;
      const cat = cell.category;

      let color: string;
      if (eliminated.has(cat)) {
        color = newlyEliminated.has(cat) ? '#7f1d1d' : '#1f2937';
      } else if (highlighted.has(cat)) {
        color = '#2563eb';
      } else {
        color = '#374151';
      }

      ctx.fillStyle = color;
      ctx.fillRect(x + 1, y + 1, CELL_SIZE - 2, CELL_SIZE - 2);
    });
  }, [snapshot, prevSnapshot]);

  return (
    <canvas
      ref={canvasRef}
      width={GRID_PX}
      height={GRID_PX}
      className="mini-grid-canvas"
      title={`${snapshot.street} — ${snapshot.highlightedCategories.length} hands`}
    />
  );
}

export function SnapshotPanel() {
  const snapshots = useSessionStore((s) => s.snapshots);
  const [collapsed, setCollapsed] = useState(false);

  if (snapshots.length === 0) return null;

  return (
    <div className="snapshot-panel">
      <div className="snapshot-panel-header" onClick={() => setCollapsed(c => !c)}>
        <span>Range History ({snapshots.length})</span>
        <span className="collapse-icon">{collapsed ? '▶' : '▼'}</span>
      </div>

      {!collapsed && (
        <div className="snapshot-list">
          {snapshots.map((snap, i) => {
            // Compute newly eliminated hands for this snapshot
            const eliminated = getEliminatedCategories(snap);
            const prevEliminated = i > 0 ? getEliminatedCategories(snapshots[i - 1]) : new Set<string>();
            const newlyEliminated = [...eliminated].filter(h => !prevEliminated.has(h));

            return (
              <div key={i} className="snapshot-item">
                <MiniGrid snapshot={snap} prevSnapshot={snapshots[i - 1]} />
                <div className="snapshot-meta">
                  <span
                    className="snapshot-street"
                    style={{ color: STREET_COLORS[snap.street] ?? '#9ca3af' }}
                  >
                    {snap.street.toUpperCase()}
                  </span>
                  <span className="snapshot-trigger">{snap.triggerLabel}</span>
                  <span className="snapshot-width">{snap.highlightedCategories.length} hands</span>
                  {newlyEliminated.length > 0 && (
                    <span className="snapshot-eliminated" title={newlyEliminated.join(', ')}>
                      -{newlyEliminated.length}: {newlyEliminated.slice(0, 5).join(', ')}
                      {newlyEliminated.length > 5 ? '...' : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
